import type { Err } from './common';
import type { Bundle, BundleKey } from './types';

/*
 * Day Zero, the tutorial (ADR 0074, tutorial contract §5, §6.1, §7): a port of the core of
 * packages/shared-cs/Runtime/Content/ContentValidator.Tutorial.cs with the same messages: `tutorialRules`' clamps and openers, at most one
 * live tutorial arc, heroIsPlayer only on a tutorial arc, the kit table's items, every trigger of a tutorial scenario `once`, and no
 * content that writes a `tutorial.*` flag. NOT mirrored (the server checks them at publish; T4's Story Lab smoke checks the opener set):
 * the "2 or 3 visible for every deal" showIf rule, the opener set, the anchor and door names, the chains' `dz` tags and the arm rows.
 * Types are local until `@argzombie/api-client` is regenerated (T2).
 */

type Cond = { fact?: string | null; args?: string[] | null } & Record<string, unknown>;
type ItemStack = { itemId?: string | null; quantity?: number | null };
export type TutorialOpenerDef = {
  id?: string | null;
  companion?: { name?: string | null; appearance?: string | null; pronouns?: string | null } | null;
  closingLine?: string | null;
  hours?: number[] | null;
};
export type TutorialRulesDef = {
  releasedAt?: string | null;
  heroTank?: number | null;
  /** Card #487: the hero's WIND floor in a Day Zero raid (client rule), 0..heroTank-10, default 10. */
  windFloor?: number | null;
  layouts?: string[] | null;
  openers?: (TutorialOpenerDef | null)[] | null;
  /** V5a THE STREET (v2.1, card #519): the wind marks, the pack's pacing and the caught count. */
  run?: {
    belowPct?: number;
    abovePct?: number;
    packSize?: number;
    holdBackM?: number;
    closeMps?: number;
    caughtGrabs?: number;
    recoverTimeoutS?: number;
  } | null;
  lock?: { holdBackM?: number } | null;
  wave?: { small?: number; big?: number } | null;
  lunge?: { telegraphS?: number; speedMps?: number; rangeM?: number; cooldownS?: number } | null;
  hints?: { oneTapS?: number; stallS?: number[] | null; repeatFirstS?: number } | null;
  collapse?: { reachM?: number } | null;
};
export type KitRowDef = { when?: Cond | null; items?: ItemStack[] | null; arm?: boolean };

/** shared-cs `TutorialLayouts.All`. */
export const TUTORIAL_LAYOUTS = ['dz_house', 'dz_block'] as const;
/** shared-cs `ContentValidator.MaxGrantQuantity`. */
const MAX_GRANT = 999;

type Loose = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export function validateTutorial(b: Bundle, err: Err) {
  const bundle = b as unknown as Loose;
  const r: TutorialRulesDef | null | undefined = bundle.tutorialRules;
  const e = (path: string, m: string) => err('tutorialRules' as BundleKey, undefined, path, m);
  if (r === null) e('', "Bundle is missing 'tutorialRules'.");
  else if (r !== undefined) rules(r, e);

  const arcs: Loose[] = (bundle.arcs ?? []).filter((a: Loose | null) => a != null);
  const live = arcs.filter((a) => a.kind === 'tutorial' && a.status === 'live');
  if (live.length > 1) err('arcs', undefined, '', `There are ${live.length} live tutorial arcs (at most one).`);
  const tutorialArcs = new Set(arcs.filter((a) => a.kind === 'tutorial').map((a) => a.id ?? ''));
  const items = new Set<string>((bundle.items ?? []).filter((i: Loose | null) => i != null).map((i: Loose) => i.id ?? ''));
  const chains = new Set<string>((bundle.beatChains ?? []).filter((c: Loose | null) => c != null).map((c: Loose) => c.id ?? ''));

  const tutorialScenarios = new Set<string>();
  (bundle.missions ?? []).forEach((m: Loose | null, i: number) => {
    const st = m?.story;
    if (st == null) return;
    const w = `Issue '${m!.id ?? ''}'`;
    const tutorial = tutorialArcs.has(st.arcId ?? '');
    if (tutorial)
      for (const sg of m!.stages ?? []) if (sg?.encounter?.scenarioId) tutorialScenarios.add(sg.encounter.scenarioId);
    if (st.heroIsPlayer && !tutorial) err('missions', i, 'story.heroIsPlayer', `${w} sets heroIsPlayer, but its arc isn't a tutorial arc.`);
    const kit: KitRowDef[] = (st.rewards?.kit ?? []).filter((k: KitRowDef | null) => k != null);
    if (kit.length > 0 && !st.heroIsPlayer)
      err('missions', i, 'story.rewards.kit', `${w} has a kit table, but only an issue with heroIsPlayer may (contract §5.1).`);
    kit.forEach((row, n) => {
      const rw = `${w} kit row ${n}`;
      const stacks = row.items ?? [];
      if (stacks.length === 0) err('missions', i, `story.rewards.kit.${n}`, `${rw} pays nothing.`);
      for (const s of stacks) {
        if (s == null || !items.has(s.itemId ?? ''))
          err('missions', i, `story.rewards.kit.${n}`, `${rw} pays unknown item '${s?.itemId ?? ''}'.`);
        else if ((s.quantity ?? 0) < 1 || (s.quantity ?? 0) > MAX_GRANT)
          err('missions', i, `story.rewards.kit.${n}`, `${rw} needs item quantity 1..${MAX_GRANT} ('${s.itemId}').`);
      }
    });
    (st.cast ?? []).forEach((c: Loose | null, n: number) => {
      if (c?.protectedUntil && !chains.has(c.protectedUntil))
        err('missions', i, `story.cast.${n}.protectedUntil`, `${w} cast '${c.castId ?? ''}' is protected until unknown beat chain '${c.protectedUntil}'.`);
    });
  });

  (bundle.scenarios ?? []).forEach((sc: Loose | null, i: number) => {
    if (sc == null) return;
    const tutorial = tutorialScenarios.has(sc.id ?? '');
    (sc.triggers ?? []).forEach((t: Loose | null, j: number) => {
      if (t == null) return;
      if (tutorial && !t.once)
        err('scenarios', i, `triggers.${j}.once`, `Scenario '${sc.id}' scenario trigger '${t.id ?? ''}' must be once (every Day Zero trigger fires once).`);
    });
    const actions: Loose[] = [...(sc.triggers ?? []).flatMap((t: Loose | null) => t?.do ?? []), ...(sc.onWin ?? []), ...(sc.onLose ?? [])];
    for (const a of actions) {
      const key = a?.type === 'set_flag' ? a.key : a?.effect?.type === 'flag' ? a.effect.flag?.key : '';
      if (typeof key === 'string' && key.startsWith('tutorial.'))
        err('scenarios', i, '', `Scenario '${sc.id}' sets '${key}' (tutorial.* flags are set by the server only).`);
    }
  });
}

function rules(r: TutorialRulesDef, e: (path: string, m: string) => void) {
  const w = 'tutorialRules';
  const range = (v: number | null | undefined, min: number, max: number, field: string) => {
    if (v === undefined) return; // absent = the C# default
    if (v === null || Number.isNaN(v) || v < min || v > max) e(field, `${w}.${field} must be ${min}..${max}.`);
  };
  if (r.releasedAt !== undefined && !/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2}))?$/.test(r.releasedAt ?? ''))
    e('releasedAt', `${w}.releasedAt must be an ISO date (2026-10-27).`);
  range(r.heroTank, 20, 100, 'heroTank');
  range(r.windFloor, 0, Math.max(0, (r.heroTank ?? 40) - 10), 'windFloor'); // card #487: at least 10 of the tank stays spendable
  for (const l of r.layouts ?? [])
    if (!(TUTORIAL_LAYOUTS as readonly string[]).includes(l))
      e('layouts', `${w}.layouts names unknown layout '${l}' (${TUTORIAL_LAYOUTS.join(', ')}).`);
  const ids = new Set<string>();
  (r.openers ?? []).forEach((o, n) => {
    if (o == null) return;
    const ow = `${w} opener '${o.id ?? ''}'`;
    if (!o.id?.trim() || ids.has(o.id)) e(`openers.${n}.id`, `${w} has a missing or duplicate opener id '${o.id ?? ''}'.`);
    else ids.add(o.id);
    if (!o.companion?.name?.trim()) e(`openers.${n}.companion.name`, `${ow} needs a companion name.`);
    if (!o.closingLine?.trim()) e(`openers.${n}.closingLine`, `${ow} needs Tyler's closing line.`);
    const h = o.hours ?? [];
    if (h.length !== 0 && (h.length !== 2 || h.some((x) => x < 0 || x > 23)))
      e(`openers.${n}.hours`, `${ow} needs hours [from, to] in 0..23, or none.`);
  });
  const run = r.run ?? {};
  range(run.belowPct, 5, 95, 'run.belowPct');
  range(run.abovePct, 5, 95, 'run.abovePct');
  if ((run.belowPct ?? 30) >= (run.abovePct ?? 70)) e('run.belowPct', `${w}.run.belowPct must be below run.abovePct.`);
  // V5a THE STREET (v2.1, card #519): the pack's pacing and the caught count; the below mark must be reachable above the floor.
  range(run.packSize, 1, 8, 'run.packSize');
  range(run.holdBackM, 4, 30, 'run.holdBackM');
  range(run.closeMps, 1, 8, 'run.closeMps');
  range(run.caughtGrabs, 1, 5, 'run.caughtGrabs');
  {
    const tank = r.heroTank ?? 40;
    const floor = Math.max(0, Math.min(r.windFloor ?? 10, Math.max(0, tank - 10)));
    const below = run.belowPct ?? 30;
    if (tank > 0 && below * tank <= floor * 100)
      e('run.belowPct', `${w}.run.belowPct (${below}% of ${tank}) must sit above the wind floor (${floor}): the hero could never reach it.`);
  }
  range(run.recoverTimeoutS, 5, 60, 'run.recoverTimeoutS');
  range(r.lock?.holdBackM, 2, 15, 'lock.holdBackM');
  range(r.wave?.small, 1, 6, 'wave.small');
  range(r.wave?.big, 1, 6, 'wave.big');
  range(r.lunge?.telegraphS, 0.6, 3, 'lunge.telegraphS');
  range(r.lunge?.speedMps, 2, 8, 'lunge.speedMps');
  range(r.lunge?.rangeM, 2, 8, 'lunge.rangeM');
  range(r.lunge?.cooldownS, 1, 15, 'lunge.cooldownS');
  range(r.hints?.oneTapS, 2, 60, 'hints.oneTapS');
  for (const s of r.hints?.stallS ?? []) range(s, 2, 120, 'hints.stallS');
  range(r.hints?.repeatFirstS, 5, 120, 'hints.repeatFirstS');
  range(r.collapse?.reachM, 1, 10, 'collapse.reachM');
}
